import React, { CSSProperties, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Auth, API, graphqlOperation } from 'aws-amplify';
import { useTenantUserInfo } from '../../context/userInfoProvider';
import { createOrder } from '../../graphql/mutations';
import { createAppointment } from '../../components/TenantApp/createAppointment';
import { v4 as uuidv4 } from 'uuid';
import { S3url } from '../../components/TenantApp/s3url';
import moment from 'moment';

type LocationState = {
  selected: string[] | string;
  type: 'DELIVERY' | 'PICKUP' | string;
  id?: string[];
  needHelp?: string;
  needPack?: string;
  moverOption?: string;
  dateSelected: string;
  timeSelected: string;
  slotPaid?: boolean;
  paidPricingLabel?: string;
};

const pageContainer: CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerBlock: CSSProperties = {
  marginBottom: 16,
};

const titleStyle: CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: CSSProperties = {
  fontSize: 13,
  opacity: 0.8,
};

const sectionHeader: CSSProperties = {
  fontSize: 13,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  opacity: 0.8,
  marginBottom: 6,
  marginTop: 12,
};

const infoRow: CSSProperties = {
  marginBottom: 8,
};

const label: CSSProperties = {
  fontSize: 12,
  opacity: 0.75,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
};

const value: CSSProperties = {
  fontSize: 15,
};

const divider: CSSProperties = {
  height: 1,
  background:
    'linear-gradient(to right, rgba(255,255,255,0.2), rgba(255,255,255,0.04))',
  margin: '16px 0',
};

const itemsRow: CSSProperties = {
  display: 'flex',
  gap: 10,
  overflowX: 'auto',
  paddingBottom: 4,
};

const itemCard: CSSProperties = {
  minWidth: 140,
  backgroundColor: '#FFF',
  color: '#111827',
  borderRadius: 8,
  overflow: 'hidden',
  border: '1px solid #E5E7EB',
};

const itemImage: CSSProperties = {
  width: '100%',
  height: 100,
  objectFit: 'cover',
};

const itemBody: CSSProperties = {
  padding: 8,
  fontSize: 12,
};

const footer: CSSProperties = {
  marginTop: 24,
  display: 'flex',
  justifyContent: 'flex-end',
};

const primaryButton: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const TenantReviewOrderPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const state = location.state;
  const {
    addressLine1,
    addressLine2,
    city,
    state: region,
    zip,
    specialInstructions,
    building,
    parking,
    facilityID,
    flatFee,
    hourlyFee,
    unitID,
  } = useTenantUserInfo() as any;

  const [email, setEmail] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Auth.currentUserInfo()
      .then(info => {
        const attrs = info?.attributes || {};
        setEmail(attrs.email || '');
      })
      .catch(err => console.error('Error fetching user info:', err));
  }, []);

  if (!state) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <h2>Missing order details</h2>
          <p>Please start again from &quot;Schedule appointment&quot;.</p>
        </div>
      </div>
    );
  }

  const {
    dateSelected,
    timeSelected,
    type,
    selected,
    needHelp = '',
    needPack = '',
    moverOption = '',
    slotPaid = false,
    paidPricingLabel,
  } = state;

  const isDelivery = type === 'DELIVERY';

  const fullAddress =
    addressLine1 || addressLine2 || city || region || zip
      ? `${addressLine1 ?? ''}${
          addressLine2 ? ', ' + addressLine2 : ''
        }, ${city ?? ''}, ${region ?? ''} ${zip ?? ''}`.trim()
      : 'No address on file yet.';

  const handleSubmit = async () => {
    if (!email) {
      window.alert('Unable to create order: missing user email.');
      return;
    }

    setSubmitting(true);
    const orderId = uuidv4();
    const apptId = uuidv4();

    try {
      const order = {
        id: orderId,
        address: {
          addressLine1: addressLine1 ?? '',
          addressLine2: addressLine2 ?? '',
          city: city ?? '',
          state: region ?? '',
          zip: zip ?? '',
          specialInstructions: specialInstructions ?? '',
          building: building ?? '',
          parking: parking ?? '',
        },
        date: dateSelected,
        facilityID,
        tenantID: email,
        jobType: type,
        status: 'PENDING',
        time: timeSelected,
        needHelp,
        needPack,
        moverOption,
        orderItems: selected,
        driver: 'None',
        isInitialOrder: !unitID,
        chosenSlotPaid: slotPaid,
        chosenSlotPrice: slotPaid ? (paidPricingLabel ?? null) : 'Free',
      };

      const appointmentData = {
        id: apptId,
        orderID: orderId,
        driver: '',
        facilityID,
        date: dateSelected,
        time: timeSelected,
        type: 'Order',
        tenantID: email,
      };

      await API.graphql(graphqlOperation(createOrder, { input: order }));
      await createAppointment(appointmentData);

      history.replace('/tenant/neworder/confirmation', {
        dateSelected,
        timeSelected,
        type,
      });
    } catch (err) {
      console.error('Error creating order/appointment:', err);
      window.alert('There was a problem submitting your order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedList =
    Array.isArray(selected) && !isDelivery
      ? selected.join(', ')
      : typeof selected === 'string'
      ? selected
      : '';

  const selectedIdsForImages = Array.isArray(selected) && isDelivery ? selected : [];

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerBlock}>
          <h1 style={titleStyle}>Review and confirm</h1>
          <div style={subtitleStyle}>
            Please confirm the details below before submitting your order.
          </div>
        </div>

        {/* Items */}
        <div>
          <div style={sectionHeader}>Items</div>
          {isDelivery ? (
            selectedIdsForImages.length === 0 ? (
              <div style={{ fontSize: 13, opacity: 0.9 }}>No items selected.</div>
            ) : (
              <div style={itemsRow}>
                {selectedIdsForImages.map(id => (
                  <div key={id} style={itemCard}>
                    <img
                      style={itemImage}
                      src={`${S3url}${email}_${id}`}
                      alt={String(id)}
                    />
                    <div style={itemBody}>ID #{id}</div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div style={infoRow}>
              <div style={label}>Items</div>
              <div style={value}>{selectedList || 'None specified'}</div>
            </div>
          )}
        </div>

        <div style={divider} />

        {/* Date / time / address / type */}
        <div>
          <div style={infoRow}>
            <div style={label}>Date</div>
            <div style={value}>
              {dateSelected
                ? moment(dateSelected).format('MMMM DD, YYYY')
                : 'N/A'}
            </div>
          </div>
          <div style={infoRow}>
            <div style={label}>Time</div>
            <div style={value}>{timeSelected || 'N/A'}</div>
          </div>
          <div style={infoRow}>
            <div style={label}>Address</div>
            <div style={value}>{fullAddress}</div>
          </div>
          <div style={infoRow}>
            <div style={label}>Order type</div>
            <div style={value}>{type}</div>
          </div>
        </div>

        <div style={divider} />

        {/* Fees */}
        <div>
          <div style={sectionHeader}>Fees</div>
          <div style={infoRow}>
            <div style={label}>Truck fee</div>
            <div style={value}>
              {flatFee ? `$${flatFee}` : 'See facility pricing'}
            </div>
          </div>
          <div style={infoRow}>
            <div style={label}>Moving fees</div>
            <div style={value}>
              {hourlyFee ? `$${hourlyFee} / hour` : 'Varies by job'}
            </div>
          </div>
        </div>

        <div style={footer}>
          <button
            type="button"
            style={primaryButton}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantReviewOrderPage;
